<script>
import HorizontalTopbar from '@/components/horizontal-topbar'
import HorizontalNav from '@/components/horizontal-navbar'
import { layoutComputed } from '@/state/helpers'
import { http } from '@/helpers/easyindustriaapi/config'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'ProfessionalContasPagar',
  components: { HorizontalTopbar, HorizontalNav },
  computed: {
    ...layoutComputed,
    competenceMonthOptions() {
      return [
        { text: 'Todos os meses', value: null },
        ...Array.from({ length: 12 }, (_, index) => ({
          text: String(index + 1).padStart(2, '0'),
          value: index + 1,
        })),
      ]
    },
    competenceYearOptions() {
      const currentYear = new Date().getFullYear()
      return [
        { text: 'Todos os anos', value: null },
        ...Array.from({ length: 7 }, (_, index) => ({
          text: String(currentYear - 5 + index),
          value: currentYear - 5 + index,
        })),
      ]
    },
  },
  data() {
    const previous = new Date()
    previous.setMonth(previous.getMonth() - 1)
    return {
      currentEmpresa: null,
      contasPagar: [],
      professional: null,
      loading: false,
      uploading: false,
      exporting: false,
      selectedConta: null,
      showDetails: false,
      error: null,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      sortBy: 'vencimento',
      sortDesc: true,
      filters: {
        vencimento_de: null,
        vencimento_ate: null,
        data_pagamento_de: null,
        data_pagamento_ate: null,
        anexo: 'sem_anexo',
        status: 'todos',
        competencia_mes: previous.getMonth() + 1,
        competencia_ano: previous.getFullYear(),
      },
      fields: [
        { key: 'vencimento', label: 'Vencimento', sortable: true, formatter: 'formatDate' },
        { key: 'data_pagamento', label: 'Pagamento', sortable: true, formatter: 'formatDate' },
        { key: 'competencia', label: 'Competência', sortable: true, formatter: 'formatCompetencia' },
        { key: 'descricao', label: 'Descrição', sortable: true },
        { key: 'valor_pagar', label: 'Valor', sortable: true, formatter: 'formatMoney', tdClass: 'text-right' },
        { key: 'status', label: 'Situação', sortable: true, tdClass: 'text-center' },
        { key: 'anexo', label: 'NFS-e', sortable: false, tdClass: 'text-center' },
        { key: 'actions', label: 'Ações', sortable: false, tdClass: 'text-center' },
      ],
    }
  },
  created() {
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const response = await http.get('/mobile/v1/professional/contas-a-pagar', {
          params: { empresa_id: this.currentEmpresa?.id, ...this.filters },
        })
        this.contasPagar = response.data.contasPagar || []
        this.professional = response.data.professional || null
        this.currentPage = 1
      } catch (error) {
        this.error = error.response?.data?.message || 'Não foi possível carregar suas contas a pagar.'
        this.contasPagar = []
      } finally {
        this.loading = false
      }
    },
    openUpload(conta) {
      this.selectedConta = conta
      this.$refs.nfseFile.click()
    },
    async uploadNfse(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || !this.selectedConta) return
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        this.error = 'Selecione um arquivo PDF da NFS-e.'
        return
      }

      this.uploading = true
      this.error = null
      try {
        const form = new FormData()
        form.append('file', file)
        await http.post(
          `/mobile/v1/professional/contas-a-pagar/${this.selectedConta.id}/nfse?empresa_id=${this.currentEmpresa.id}`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        await this.load()
      } catch (error) {
        this.error = error.response?.data?.message || 'Não foi possível anexar o PDF da NFS-e.'
      } finally {
        this.uploading = false
        this.selectedConta = null
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR')
    },
    formatCompetencia(value, key, item) {
      if (!item.ano_competencia || !item.mes_competencia) return '-'
      return `${String(item.mes_competencia).padStart(2, '0')}/${item.ano_competencia}`
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    hasAttachment(conta) {
      return Array.isArray(conta.arquivos) && conta.arquivos.length > 0
    },
    openDetails(conta) {
      this.selectedConta = conta
      this.showDetails = true
    },
    formatStatus(status) {
      return status === 1 ? 'Pago' : 'Aberto'
    },
    formatPaymentType(conta) {
      return conta.tipoPagamento?.descricao || conta.tipoPagamento?.name || conta.tipo_pagamento_id || '-'
    },
    formatSupplier(conta) {
      return conta.fornecedor?.nome || conta.fornecedor?.name || conta.fornecedor_id || '-'
    },
    formatHistory(conta) {
      return conta.historicoPadrao?.descricao || conta.historicoPadrao?.name || conta.historico_padrao_id || '-'
    },
    exportPdf() {
      if (!this.contasPagar.length) {
        this.error = 'Não há lançamentos filtrados para exportar.'
        return
      }

      this.exporting = true
      try {
        const doc = new jsPDF('landscape')
        const generatedAt = new Date().toLocaleString('pt-BR')
        const filterSummary = [
          `Vencimento: ${this.filters.vencimento_de || 'todos'} até ${this.filters.vencimento_ate || 'todos'}`,
          `Pagamento: ${this.filters.data_pagamento_de || 'todos'} até ${this.filters.data_pagamento_ate || 'todos'}`,
          `Anexo: ${this.filters.anexo || 'todos'}`,
          `Situação: ${this.filters.status || 'todos'}`,
          `Competência: ${this.filters.competencia_mes || 'todos'}/${this.filters.competencia_ano || 'todos'}`,
        ].join(' | ')

        doc.setFontSize(16)
        doc.text('Meus repasses', 14, 16)
        doc.setFontSize(9)
        doc.text(`Profissional: ${this.professional?.name || '-'}`, 14, 23)
        doc.text(`Gerado em: ${generatedAt}`, 14, 29)
        doc.text(filterSummary, 14, 35, { maxWidth: 268 })

        doc.autoTable({
          startY: 42,
          head: [['Vencimento', 'Pagamento', 'Competência', 'Descrição', 'Valor', 'Situação', 'NFS-e']],
          body: this.contasPagar.map((conta) => [
            this.formatDate(conta.vencimento),
            this.formatDate(conta.data_pagamento),
            this.formatCompetencia(null, null, conta),
            conta.descricao || '-',
            this.formatMoney(conta.valor_pagar),
            conta.status === 1 ? 'Pago' : 'Aberto',
            this.hasAttachment(conta) ? 'Com anexo' : 'Sem anexo',
          ]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [41, 128, 185] },
          columnStyles: { 3: { cellWidth: 70 }, 4: { halign: 'right' } },
        })

        doc.save(`meus-repasses-${new Date().toISOString().slice(0, 10)}.pdf`)
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<template>
  <div id="layout-wrapper">
    <HorizontalTopbar :type="topbar" :width="layoutWidth" />
    <HorizontalNav />
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid professional-contas-pagar">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 class="mb-1">Meus repasses</h4>
              <small v-if="professional" class="text-muted">{{ professional.name }}</small>
            </div>
            <div class="d-flex flex-wrap justify-content-end">
              <b-button variant="outline-danger" size="sm" class="mr-2 mb-1" :disabled="loading || exporting || !contasPagar.length" @click="exportPdf">
                <b-spinner v-if="exporting" small class="mr-1" />
                <i v-else class="ri-file-pdf-line mr-1" /> Exportar PDF
              </b-button>
              <b-button variant="outline-primary" size="sm" class="mb-1" :disabled="loading" @click="load">
                <i class="ri-refresh-line mr-1" /> Atualizar
              </b-button>
            </div>
          </div>

          <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

          <b-card class="mb-3">
            <b-form @submit.prevent="load">
              <div class="row">
                <div class="col-md-3">
                  <b-form-group label="Vencimento de">
                    <b-form-input v-model="filters.vencimento_de" type="date" />
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group label="Vencimento até">
                    <b-form-input v-model="filters.vencimento_ate" type="date" />
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group label="Pagamento de">
                    <b-form-input v-model="filters.data_pagamento_de" type="date" />
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group label="Pagamento até">
                    <b-form-input v-model="filters.data_pagamento_ate" type="date" />
                  </b-form-group>
                </div>
              </div>
              <div class="row align-items-end">
                <div class="col-md-3">
                  <b-form-group label="Anexo">
                    <b-form-select v-model="filters.anexo" :options="[
                      { text: 'Sem anexo', value: 'sem_anexo' },
                      { text: 'Com anexo', value: 'com_anexo' },
                      { text: 'Todos', value: 'todos' }
                    ]" />
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group label="Situação">
                    <b-form-select v-model="filters.status" :options="[
                      { text: 'Aberto', value: 'aberto' },
                      { text: 'Pago', value: 'pago' },
                      { text: 'Todos', value: 'todos' }
                    ]" />
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Mês competência">
                    <b-form-select v-model="filters.competencia_mes" :options="competenceMonthOptions" />
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Ano competência">
                    <b-form-select v-model="filters.competencia_ano" :options="competenceYearOptions" />
                  </b-form-group>
                </div>
                <div class="col-md-2 mb-3">
                  <b-button type="submit" variant="primary" block :disabled="loading">
                    <b-spinner v-if="loading" small class="mr-1" /> Filtrar
                  </b-button>
                </div>
              </div>
            </b-form>
          </b-card>

          <b-card>
            <input ref="nfseFile" type="file" accept=".pdf,application/pdf" class="d-none" @change="uploadNfse">
            <div class="table-responsive">
              <b-table
                :items="contasPagar"
                :fields="fields"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :per-page="perPage"
                :current-page="currentPage"
                :busy="loading"
                responsive="sm"
                striped
                hover
                show-empty
                empty-text="Nenhum lançamento encontrado para os filtros selecionados."
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2"><b-spinner class="align-middle mr-2" /> Carregando...</div>
                </template>
                <template #cell(status)="row">
                  <b-badge :variant="row.item.status === 1 ? 'success' : 'warning'">
                    {{ row.item.status === 1 ? 'Pago' : 'Aberto' }}
                  </b-badge>
                </template>
                <template #cell(anexo)="row">
                  <div v-if="hasAttachment(row.item)" class="d-flex flex-column align-items-center">
                    <b-badge variant="success">Com anexo</b-badge>
                    <a
                      v-for="arquivo in row.item.arquivos"
                      :key="arquivo.id"
                      :href="arquivo.url"
                      target="_blank"
                      rel="noopener"
                      class="small mt-1"
                    >
                      <i class="ri-file-pdf-line mr-1" /> Visualizar
                    </a>
                  </div>
                  <b-badge v-else variant="secondary">Sem anexo</b-badge>
                </template>
                <template #cell(actions)="row">
                  <div class="d-flex flex-column align-items-center">
                    <b-button size="sm" variant="outline-info" class="mb-1" @click="openDetails(row.item)">
                      <i class="ri-file-search-line mr-1" /> Detalhes
                    </b-button>
                    <b-button size="sm" variant="outline-primary" :disabled="uploading" @click="openUpload(row.item)">
                      <i class="ri-upload-2-line mr-1" /> Anexar PDF
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>
            <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
              <small class="text-muted">{{ contasPagar.length }} lançamento(s)</small>
              <div class="d-flex align-items-center">
                <b-form-select v-model="perPage" :options="pageOptions" size="sm" class="mr-2 w-auto" />
                <b-pagination v-model="currentPage" :total-rows="contasPagar.length" :per-page="perPage" size="sm" class="mb-0" />
              </div>
            </div>
          </b-card>

          <b-modal v-model="showDetails" title="Detalhes do repasse" size="lg" hide-footer>
            <div v-if="selectedConta">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <strong>Descrição</strong>
                  <div>{{ selectedConta.descricao || '-' }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Documento</strong>
                  <div>{{ selectedConta.numero_documento || '-' }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Valor</strong>
                  <div>{{ formatMoney(selectedConta.valor_pagar) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Vencimento</strong>
                  <div>{{ formatDate(selectedConta.vencimento) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Pagamento</strong>
                  <div>{{ formatDate(selectedConta.data_pagamento) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Emissão</strong>
                  <div>{{ formatDate(selectedConta.data_emissao) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Situação</strong>
                  <div>{{ formatStatus(selectedConta.status) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Competência</strong>
                  <div>{{ formatCompetencia(null, null, selectedConta) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Parcelas</strong>
                  <div>{{ selectedConta.quantidade_parcelas || '-' }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Primeira parcela</strong>
                  <div>{{ formatDate(selectedConta.data_primeira_parcela) }}</div>
                </div>
                <div class="col-md-3 mb-3">
                  <strong>Forma de pagamento</strong>
                  <div>{{ formatPaymentType(selectedConta) }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <strong>Fornecedor</strong>
                  <div>{{ formatSupplier(selectedConta) }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <strong>Histórico</strong>
                  <div>{{ formatHistory(selectedConta) }}</div>
                </div>
                <div class="col-12 mb-3">
                  <strong>Observação</strong>
                  <div class="text-pre-wrap">{{ selectedConta.observacao || '-' }}</div>
                </div>
                <div class="col-12">
                  <strong>Documentos anexados</strong>
                  <div v-if="!hasAttachment(selectedConta)" class="text-muted mt-2">Nenhum documento anexado.</div>
                  <ul v-else class="list-group mt-2">
                    <li v-for="arquivo in selectedConta.arquivos" :key="arquivo.id" class="list-group-item d-flex justify-content-between align-items-center">
                      <span>{{ arquivo.nome }}</span>
                      <a :href="arquivo.url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-danger">
                        <i class="ri-file-pdf-line mr-1" /> Visualizar PDF
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.professional-contas-pagar .card { border-radius: 0.35rem; }
.professional-contas-pagar .table { min-width: 760px; }
.professional-contas-pagar .text-pre-wrap { white-space: pre-wrap; }
</style>
